/**
 * The shell's icon set, ported from the feel-test mockup so the built interface
 * matches what we already agreed looked calm. Plain stroked SVGs, currentColor
 * so they pick up the glass-button colour.
 */
import type { Mode } from '@/store/shell';

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function ModeIcon({ mode, size = 24 }: { mode: Mode; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', ...stroke };
  switch (mode) {
    case 'Plotting':
      return (
        <svg {...common}>
          <path d="M9 3 L3 6 v15 l6-3 6 3 6-3 V3 l-6 3 Z" />
          <path d="M9 3 v15 M15 6 v15" />
        </svg>
      );
    case 'Training':
      return (
        <svg {...common}>
          <path d="M3 8 L12 4 L21 8 L12 12 Z" />
          <path d="M7 10 v5 c0 1 10 1 10 0 v-5" />
        </svg>
      );
    case 'Live':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M5 5 a10 10 0 0 0 0 14 M19 5 a10 10 0 0 1 0 14" />
        </svg>
      );
  }
}

export function LayersIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <polygon points="12 2 22 8.5 12 15 2 8.5 12 2" />
      <polyline points="2 15.5 12 22 22 15.5" />
    </svg>
  );
}

export function RouteIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <circle cx="5" cy="19" r="2.4" />
      <circle cx="19" cy="5" r="2.4" />
      <path d="M7 18 C 12 16, 9 9, 14 7" />
    </svg>
  );
}

export function RecentreIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="12" r="3.5" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
    </svg>
  );
}

/** Small tool glyphs for the plotting palette. */
export function ToolGlyph({ tool, size = 22 }: { tool: string; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', ...stroke };
  switch (tool) {
    case 'measure':
      return (
        <svg {...common}>
          <path d="M3 17 L21 7" />
          <path d="M5 14 v3 M9 12 v3 M13 10 v3 M17 8 v3" />
        </svg>
      );
    case 'fix':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 4 v16 M4 12 h16" />
        </svg>
      );
    case 'dr':
      return (
        <svg {...common}>
          <path d="M4 20 L20 4" />
          <circle cx="4" cy="20" r="2" />
        </svg>
      );
    case 'ep':
      return (
        <svg {...common}>
          <path d="M4 20 L16 8" />
          <polygon points="16,5 20,12 12,12" />
        </svg>
      );
    case 'vrm':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      );
    case 'ebl':
      return (
        <svg {...common}>
          <circle cx="5" cy="19" r="2" />
          <path d="M6 18 L20 5" />
        </svg>
      );
    case 'running_fix':
      return (
        <svg {...common}>
          <circle cx="8" cy="12" r="5" />
          <circle cx="17" cy="12" r="5" />
          <path d="M3 19 h18" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

/** A chevron, pointing left or right, for breadcrumb back and drill-in rows. */
export function ChevronIcon({
  dir = 'right',
  size = 20,
}: {
  dir?: 'left' | 'right';
  size?: number;
}) {
  const d = dir === 'left' ? 'M15 6 L9 12 L15 18' : 'M9 6 L15 12 L9 18';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d={d} />
    </svg>
  );
}

/** The three-line burger, opening the main menu at its top level. */
export function BurgerIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

/**
 * A ring lifebuoy (the classic orange-and-white "perry buoy"), for the MOB
 * button. Drawn with its own colours rather than currentColor so it always reads
 * as a life-ring on the frosted-white button: an orange ring with the four white
 * grab-bands at the cardinal points, and the hole left open so it sits on any
 * background.
 */
export function LifebuoyIcon({ size = 26 }: { size?: number }) {
  const ring = {
    fill: 'none',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      {/* orange ring */}
      <circle cx="12" cy="12" r="8" stroke="#E8552B" strokeWidth="3.6" {...ring} />
      {/* four white grab-bands across the ring at N/E/S/W */}
      <g stroke="#ffffff" strokeWidth="3.6" strokeLinecap="butt">
        <line x1="12" y1="2.2" x2="12" y2="5.8" />
        <line x1="12" y1="18.2" x2="12" y2="21.8" />
        <line x1="2.2" y1="12" x2="5.8" y2="12" />
        <line x1="18.2" y1="12" x2="21.8" y2="12" />
      </g>
    </svg>
  );
}

/** A ship's anchor, for the ready-to-drop HUD button. */
export function AnchorIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      {/* ring at the top of the shank */}
      <circle cx="12" cy="4" r="2" />
      {/* the shank running down the middle */}
      <line x1="12" y1="6" x2="12" y2="20" />
      {/* the stock across the shank */}
      <line x1="8" y1="9" x2="16" y2="9" />
      {/* the curved arms/flukes sweeping up from the crown */}
      <path d="M5 13 a7 7 0 0 0 7 7 a7 7 0 0 0 7 -7" />
    </svg>
  );
}

/** A microphone, for the voice trigger. */
export function VoiceIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11 a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="21" />
    </svg>
  );
}
